/**
 * Shared helpers for the Reporting V2 grids (ReportDataGrid + ReportGroupedGrid).
 *
 * Centralizing these keeps the two sibling grids from drifting — e.g. the
 * "forgiving" per-column filter (CC-49379) must behave identically whether a
 * report renders flat or grouped.
 */

// Spec 171: the column filter's operator vocabulary, shared with the picker
// that edits it (common/ may not import from reports/, so it lives there).
// A RELATIVE path, never the `@components` alias: cb-api's own test suite imports
// this file (export-filters-parity.test.js) under a Vite config with no app aliases,
// and an alias here made that whole test file fail to load.
import {
  MATCH_STARTS_WITH,
  columnFilterMode,
  isAnchoredMode,
} from '../common/columnFilterMatchMode.js'
import { escapeHtml } from './escapeHtml.js'

// The handful of entities report values carry after esc()/CB markup. Decode
// them so text sorts and filters as the user reads it — a name stored with a
// leading quote must not sort as "&quot;…" (CC-49753 issue 2). &amp; last so
// double-encoded values don't over-decode.
const decodeEntities = (s) => s
  .replace(/&quot;/g, '"')
  .replace(/&#0?39;/g, "'")
  .replace(/&nbsp;/g, ' ')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&amp;/g, '&')

/** Strip CB's HTML markup from a displayValue so it can be matched/measured as text. */
export const stripHtml = (s) => decodeEntities(String(s ?? '').replace(/<[^>]*>/g, ''))

/**
 * Shared sort comparator (CC-49753): the V1 default order — numbers first,
 * then digit-leading text (0-9), then letters (A-Z); case- and punctuation-
 * insensitive with natural number runs ("2" < "10"); blanks last. Both grids,
 * client-side group ordering, and the AR export sort route through this so
 * flat / grouped / specialized reports order identically.
 */
export const compareCellValues = (a, b) => {
  const aNum = typeof a === 'number'
  const bNum = typeof b === 'number'
  if (aNum && bNum) return a - b
  if (aNum !== bNum) return aNum ? -1 : 1 // numeric values sort before any text
  const sa = stripHtml(String(a ?? '')).trim().toLowerCase()
  const sb = stripHtml(String(b ?? '')).trim().toLowerCase()
  if (sa === sb) return 0
  if (!sa || !sb) return sa ? -1 : 1 // blanks last
  // Classify by the first alphanumeric char so leading quotes/whitespace can't
  // eject a name from its alphabetical position (Aged A/R "Ford, Powell…" bug).
  const lead = (s) => (s.match(/[a-z0-9]/) || [''])[0]
  const la = lead(sa)
  const lb = lead(sb)
  const da = la >= '0' && la <= '9'
  const db = lb >= '0' && lb <= '9'
  if (da !== db) return da ? -1 : 1 // digit-leading before letter-leading (V1: 0-9 → A-Z)
  return sa.localeCompare(sb, 'en', { numeric: true, sensitivity: 'base', ignorePunctuation: true })
}

/**
 * Forgiving per-column filter (CC-49379), used by BOTH report grids.
 * Matches the formatted text the user sees. Tries a literal substring match
 * first, then retries with $ , % and whitespace stripped from both sides — so
 * on a currency column "205,253", "205253" and "$205,253" all match "205,253.96".
 * Text columns are unaffected (nothing to strip).
 */
export const forgivingMatch = (shown, query) => {
  if (!query) return true
  const s = stripHtml(shown).toLowerCase()
  const q = String(query).toLowerCase()
  if (s.includes(q)) return true
  const strip = (x) => x.replace(/[$,%\s]/g, '')
  return strip(s).includes(strip(q))
}

/**
 * Spec 171 — the per-column filter's operator: Contains / Starts with / Ends with.
 *
 * Contains IS `forgivingMatch`, delegated to unchanged, so the column filter's
 * default (and every view saved before this spec) matches exactly as it did.
 *
 * Starts with / Ends with are the same forgiving matcher, ANCHORED:
 *   1. case-insensitive, against the text the user SEES (HTML stripped), trimmed
 *      first — CB pads some cells, and " Smith" should start with "Smith";
 *   2. then the same retry `forgivingMatch` makes, with `$ , %` and whitespace
 *      stripped from BOTH sides, anchored again — so "Starts with 1,2" and
 *      "Starts with 12" both find "$1,234.00", and "Ends with 5" finds "12.5%".
 * Step 2 DECIDES every outcome: stripping is character-wise, so whenever step 1
 * matches, step 2 does too (exactly as in `forgivingMatch`), and step 2 already
 * drops all whitespace. Step 1 and its `.trim()` are a fast path that reads the
 * way the rule is stated — removing the trim changes no result, and spec 171's
 * mutation run records that mutant as equivalent rather than as a missing test.
 * Parentheses are NOT stripped, deliberately (spec 171 §3): a negative shown as
 * "(1,234.00)" starts with "(" and ends with ")", so "Ends with .00" does not
 * find it — the filter matches what is on screen, and a sign is not punctuation.
 *
 * Any mode other than the two anchored ones is Contains (columnFilterMode).
 */
export const matchByMode = (shown, query, mode) => {
  if (!isAnchoredMode(mode)) return forgivingMatch(shown, query)
  if (!query) return true
  const s = stripHtml(shown).trim().toLowerCase()
  const q = String(query).toLowerCase()
  const anchored = mode === MATCH_STARTS_WITH
    ? (a, b) => a.startsWith(b)
    : (a, b) => a.endsWith(b)
  if (anchored(s, q)) return true
  const strip = (x) => x.replace(/[$,%\s]/g, '')
  return anchored(strip(s), strip(q))
}

/**
 * Sortable value for a cell (CC-49378 numeric awareness). Prefers the raw
 * dataValue so currency/number columns sort by value; date-like dataValues
 * sort by their timestamp; returns the display fallback when there's nothing
 * to parse.
 *
 * CC-49869: this must parse the WHOLE value, not a numeric prefix. CB sends
 * date dataValues as strings ("2025-07-28T00:00:00", "7/28/2025") and the old
 * parseFloat read them as 2025 / 7 — after a date-range run every row in the
 * range carried the SAME sort key, so clicking the header visibly did nothing.
 */
const DATE_LIKE_VALUE_RE = /^(\d{4}-\d{2}-\d{2}([T ].*)?|\d{1,2}\/\d{1,2}\/\d{2,4})$/
export const numericSortValue = (raw, displayFallback = '') => {
  if (raw == null) return displayFallback
  if (typeof raw === 'number') return raw
  const s = String(raw).trim()
  if (s === '') return raw
  // Strict numeric: after dropping $ , % and whitespace, the ENTIRE remainder
  // must be a number (Number(), unlike parseFloat, rejects trailing garbage).
  const cleaned = s.replace(/[$,%\s]/g, '')
  const n = Number(cleaned)
  if (cleaned !== '' && Number.isFinite(n)) return n
  // Date-like (ISO or US m/d/y) → epoch ms, so dates sort chronologically.
  // The shape check keeps ordinary text ("May 2026 Retainer") out of Date.parse.
  if (DATE_LIKE_VALUE_RE.test(s)) {
    const t = Date.parse(s)
    if (Number.isFinite(t)) return t
  }
  return raw
}

/** Parse a number out of a value that may be numeric or a formatted string. Returns null if not numeric. */
export const toNumber = (v) => {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(stripHtml(v).replace(/[$,%\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

/**
 * Build a formatter that mimics a sample formatted cell's $ prefix, thousands
 * grouping and decimal count, so a recomputed total renders like the column's
 * other cells (e.g. "205,253.96" or "$1,200.00").
 */
export const formatLikeSample = (sample) => {
  const text = stripHtml(sample).trim()
  const hasDollar = /\$/.test(text)
  const m = text.match(/\.(\d+)/)
  const decimals = m ? m[1].length : 0
  return (n) => {
    const sign = n < 0 ? '-' : ''
    const body = Math.abs(n).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return `${sign}${hasDollar ? '$' : ''}${body}`
  }
}

/**
 * SUM-only guardrail for the "recompute totals" toggle.
 *
 * Decides which columns are SAFE to re-sum when rows are filtered. A column is
 * safe only if its server total already equals the sum of EVERY row's numeric
 * value — i.e. the server total IS a plain sum. Average / rate / weighted /
 * count columns won't match and are deliberately excluded, so the toggle can
 * never fabricate a wrong total for a non-additive column.
 *
 * When CB's column metadata declares a footer aggregate (CC-49302), it is
 * authoritative: only a declared `SUM` is recomputable; `AVG/MIN/MAX/COUNT` are
 * excluded outright. The sum-equals-total heuristic is used only when no
 * aggregate is declared (`NONE`/missing).
 *
 * @param {Map<string, number[]>} allRowValuesByCol numeric values of every leaf row, per column
 * @param {Map<string, number>}   serverTotalsByCol parsed server total, per column
 * @param {Map<string, string>}   [aggregateByCol]  declared aggregate name per column (SUM/AVG/...)
 * @returns {Set<string>} column ids that are safe to recompute
 */
export const summableColumns = (allRowValuesByCol, serverTotalsByCol, aggregateByCol = null, epsilon = 0.01) => {
  const safe = new Set()
  for (const [colId, serverTotal] of serverTotalsByCol.entries()) {
    const vals = allRowValuesByCol.get(colId)
    if (!vals || vals.length === 0 || serverTotal == null) continue
    const agg = aggregateByCol?.get(colId)
    if (agg && agg !== 'NONE') {
      // Authoritative metadata: trust a declared SUM, exclude any other aggregate.
      if (agg === 'SUM') safe.add(colId)
      continue
    }
    // No declared aggregate → fall back to the sum-equals-total heuristic.
    // CC-49354 #5: the heuristic needs at least 2 rows — on a 1-row report every
    // aggregate coincides (AVG = MIN = MAX = SUM), so sum-equals-total would
    // misclassify a non-additive column as summable.
    if (vals.length < 2) continue
    const sum = vals.reduce((a, b) => a + b, 0)
    const tol = Math.max(epsilon, Math.abs(serverTotal) * 1e-6)
    if (Math.abs(sum - serverTotal) <= tol) safe.add(colId)
  }
  return safe
}

// --- Column metadata (CC-49302) -------------------------------------------
// CB returns these on each column (enriched by cb-api's enrichColumn):
//   filterTypeName: TEXT | NUMBER | DATE | DROPDOWN | BOOL | NONE | HIDDEN | ...
//   reportFooterAggregateName / groupFooterAggregateName: SUM | AVG | MIN | MAX | COUNT | NONE

const FILTER_TYPE_LABEL = {
  TEXT: 'Text', NUMBER: 'Number', NUMBERPICKER: 'Number', DATE: 'Date',
  SIMPLEDATE: 'Date', BOOL: 'Yes/No', DROPDOWN: 'Dropdown', RADIO: 'Choice',
  CHECKBOX: 'Choice', ACCOUNTSDROPDOWN: 'Account', LOOKUP: 'Lookup', TAGMANAGER: 'Tags',
}
const AGGREGATE_LABEL = { SUM: 'Sum', AVG: 'Average', MIN: 'Minimum', MAX: 'Maximum', COUNT: 'Count' }

/** The declared footer aggregate for a column (report-level preferred), or null. */
export const columnAggregate = (col) => {
  const r = col?.reportFooterAggregateName
  if (r && r !== 'NONE') return r
  const g = col?.groupFooterAggregateName
  if (g && g !== 'NONE') return g
  return null
}

/**
 * Whether a per-column quick-filter should be offered. The grid filter is
 * CLIENT-side and matches the formatted display text, so it works on ANY visible
 * column regardless of the server's filterType — only a truly HIDDEN type is
 * skipped (and those columns aren't rendered anyway). Note: filterType NONE
 * (common on computed numeric columns) is still filterable here.
 */
export const isColumnFilterable = (col) => col?.filterTypeName !== 'HIDDEN'

// --- CB's declared column type (spec 133) -----------------------------------
//
// Every generic-engine column carries CB's own `dataType`. Measured across all
// 119 generic definitions on vladdemo (958 visible columns): DECIMAL, INTEGER,
// TEXT, DATE, DATETIME, BOOL, GUID — always upper-case — or '' on the 1099 Box
// columns. The page adapters (AP, TB, AR, …) set no `dataType`, except AR's own
// raw columns, which use a lower-case 'number' / 'date' of their own.
//
// Matching is EXACT-CASE against CB's vocabulary on purpose: anything else is
// "not declared", so an adapter's lower-case 'text' / 'date' / 'number' keeps
// today's alignment rule instead of being read as CB's word for it.
export const CB_DECLARED_TYPES = new Set(['DECIMAL', 'INTEGER', 'TEXT', 'DATE', 'DATETIME', 'BOOL', 'GUID'])
// The declared types that can never hold a number. CB's own server export writes
// a number cell only for DECIMAL (ExportHelper.cs:4324) and a date cell for
// DATE/DATETIME; a TEXT invoice number or matter name is text however CB aligns it.
const CB_NEVER_NUMERIC_TYPES = new Set(['TEXT', 'DATE', 'DATETIME', 'BOOL', 'GUID'])
const CB_AMOUNT_TYPES = new Set(['DECIMAL', 'INTEGER'])

/** CB's declared column type (exact-case member of CB_DECLARED_TYPES), or null. */
export const declaredType = (col) => {
  const t = col?.dataType
  return typeof t === 'string' && CB_DECLARED_TYPES.has(t) ? t : null
}

/** CB declares this column TEXT / DATE / DATETIME / BOOL / GUID. */
export const isDeclaredNonNumeric = (col) => CB_NEVER_NUMERIC_TYPES.has(declaredType(col))

/** CB declares this column DECIMAL or INTEGER. */
export const isDeclaredAmount = (col) => CB_AMOUNT_TYPES.has(declaredType(col))

/**
 * Whether a column holds numeric/currency values. CB right-aligns numbers, and
 * numeric report columns often carry filterType NONE — so alignment is the
 * reliable signal, with the numeric filter types as a backstop. Used by the
 * export (CC-49370) to write real numeric cells (summable in Excel) instead of
 * formatted strings, and shared so the flat + grouped grids agree.
 *
 * Spec 133 — the LOSE side of CB's declared type, and only that. A column CB
 * declares TEXT / DATE / DATETIME / BOOL / GUID is never numeric, whatever its
 * alignment: Expense Report by Matter (44) right-aligns Date, Billing Code and
 * Invoice #, and Expense Report By Billing Code (222) right-aligns Date, Matter
 * Name and Invoice #, so the prefix parse in `toNumber` turned "12/30/2025"
 * (dataValue "2025-12-30T00:00:00") into 2025, "6238" into 6,238 and
 * "721 Pacific Avenue - 3797.0002" into 721 — in every client export, and into
 * "$2,025.00" / "$721.00" on screen under Show $.
 *
 * DECIMAL / INTEGER columns and undeclared columns keep the alignment rule
 * UNCHANGED. Promoting a LEFT/CENTER DECIMAL column to numeric (the "gain" side:
 * new Show $ / Hide $0 toggles on ~40 reports, amount columns leaving Group By)
 * is held for a product decision — see spec 133 §9.
 */
export const isNumericColumn = (col) =>
  !isDeclaredNonNumeric(col) &&
  (col?.columnAlignment === 'RIGHT' || NUMERIC_FILTER_TYPES.has(col?.filterTypeName))

/**
 * The CC-49872 fallback — "a numeric server GRAND total marks its column
 * numeric" — for a column the metadata rule did not already call numeric. It
 * exists for CENTER-aligned, filterType-NONE amounts (Payment/Credit Allocation's
 * Amount); it must never make a column CB DECLARES non-numeric numeric again, or
 * a date whose footer parses would come straight back as 2,025 (spec 133).
 */
export const footerMarksNumeric = (col, footerParses) => !isDeclaredNonNumeric(col) && !!footerParses

/**
 * Spec 133 — a numeric column the "Show $ on amounts" toggle must never put a
 * currency symbol on. ONE predicate for every Show $ site (both grids' cells and
 * footers, and the export views' `currency` flag):
 *   - CB CellsFormat 'P' — a fraction shown as a fraction ("0.030" is 3%, and
 *     "$0.03" is a different, wrong statement);
 *   - CB dataType INTEGER — a count. All 5 INTEGER columns in the vladdemo
 *     census are counts (222 Quantity, 52 Top % Grouping, 175/193 Years Since
 *     Graduation, 273 Days of Interest), and CB's own server export writes
 *     INTEGER as text, never as money;
 *   - an adapter column's explicit `currency: false` (AP Detail's Past Due, a day
 *     count, rendered "$2,845.00" under Show $).
 * It only withholds the symbol; the column stays numeric and its figures stay
 * real numbers.
 */
export const neverCurrency = (col) =>
  col?.cellsFormat === 'P' || declaredType(col) === 'INTEGER' || col?.currency === false

/**
 * CC-53905 (spec 155) — the columns whose source already marks them as currency:
 * some cell, subtotal or total of the column carries a "$" in the text the source
 * sent. Only consulted for a column that carries NO CellsFormat (an adapter's
 * column — AR, CSV-backed and auto-column pages); see `currencyEligible`.
 *
 * Measured: Aged A/R's adapter writes "$" on every numeric footer, Time Entries'
 * CB CSV writes "$126,006.10" on Amount's Grand Total only (its Worked Hours /
 * Billed Hours / Rate never carry one), and the auto-column pages render money
 * through `fmtMoney` ("$…") and hours / counts through `fmtNumber` (no "$").
 *
 * @param {object} report  the grid contract ({ reportData, reportFooterCells, reportFooterRows? })
 * @returns {Set<string>}  column ids, as strings
 */
export const currencyMarkedColumnIds = (report) => {
  const marked = new Set()
  const mark = (cells) => {
    for (const c of cells || []) {
      if (c && displayHasCurrency(c.displayValue)) marked.add(String(c.columnId))
    }
  }
  const walk = (rows) => {
    for (const r of rows || []) {
      if (r?.isGroup) {
        mark(r.groupFooterCells)
        for (const line of r.groupFooterRows || []) mark(line)
        walk(r.dataRows)
      } else {
        mark(r?.dataCells)
      }
    }
  }
  walk(report?.reportData)
  mark(report?.reportFooterCells)
  for (const line of report?.reportFooterRows || []) mark(line)
  return marked
}

/**
 * CC-53905 (spec 155) — may "Show $ on amounts" put a "$" on this (numeric)
 * column? Decided by a DECLARATION, never by header text ("Hours") or by the
 * shape of the numbers:
 *
 *   1. `neverCurrency` (spec 133) — CellsFormat 'P', an INTEGER count, or an
 *      adapter's `currency: false` — never.
 *   2. an adapter's explicit `currency: true` — yes.
 *   3. CB's CellsFormat, which EVERY generic-engine column carries ('' included):
 *      'C' only. This is V1's own rule. Under the tenant setting
 *      ShowCurrencySymbolOnAccountingReports, CB renders a DECIMAL column with
 *      CellsFormat + CellsFormatDecimals (ReportsController.FormattedDisplayValue,
 *      and CBServerApi ReportHelper for footers), so "C0" reads "$1,541,996" and
 *      "N1" reads "5,069.4" with no symbol, and an empty format renders "F2" with
 *      no symbol. Hours, quantities, rates and every N / F / '' column therefore
 *      stay unchanged — V1 never puts a "$" on them either.
 *   4. no CellsFormat at all (an adapter's column): only if the column's own
 *      source already marks it currency (`currencyMarkedColumnIds`). Absent any
 *      signal the display is left exactly as sent.
 *
 * The 'C' test ignores case and surrounding blanks, as .NET does: V1 formats with
 * `CellsFormat + CellsFormatDecimals`, and "c0" is the same currency format as
 * "C0". Every format in the vladdemo census is upper-case; definitions are per
 * tenant.
 *
 * @param {object} col          a grid column
 * @param {Set<string>} [marked] `currencyMarkedColumnIds(report)`
 */
export const currencyEligible = (col, marked) => {
  if (neverCurrency(col)) return false
  if (col?.currency === true) return true
  if (typeof col?.cellsFormat === 'string') return col.cellsFormat.trim().toUpperCase() === 'C'
  return !!marked?.has(String(col?.columnId))
}

/**
 * CC-53905 (spec 155, review fix 4) — the columns whose report GRAND total the
 * source sent as a number ("0" and "0.00" included), read from
 * `reportFooterCells` exactly as both export views read it (CC-49872).
 *
 * @param {object} report  the grid contract ({ reportFooterCells })
 * @returns {Set<string>}  column ids, as strings
 */
export const numericTotalColumnIds = (report) => new Set(
  (report?.reportFooterCells || [])
    .filter((c) => toNumber(c?.displayValue) != null)
    .map((c) => String(c.columnId))
)

/**
 * CC-53905 (spec 155, review fix 4) — the ONE numeric test every "Show $ on
 * amounts" site shares: both grids' cells, subtotals and totals, the toggle's
 * own presence (`currencyToggleActs`), and both export views (which also use it
 * to write real numbers). A column is numeric here when the metadata rule says so
 * (`isNumericColumn`) or when its grand total is a number (CC-49872's
 * `footerMarksNumeric`, which never overrides a type CB declares non-numeric).
 *
 * Why one test: the grids used the metadata rule alone while the export views
 * also counted a totaled column, so on Closed Matters Analysis (52) — whose C2
 * Fees Collected is LEFT-aligned, filterType NONE, with a SUM total — the toggle
 * was hidden while a saved ON still put a "$" on that column in the PDF and in
 * "export what you see" files. With one test the row is offered exactly where the
 * setting acts, and the screen and the files put the "$" on the same columns.
 *
 * It decides Show $ only. Alignment, Hide $0 and Group By keep `isNumericColumn`
 * (spec 133 §9 holds promoting LEFT / CENTER amounts there).
 *
 * @param {object} col                 a grid column
 * @param {Set<string>} [totaledIds]   `numericTotalColumnIds(report)`
 */
export const showDollarNumeric = (col, totaledIds) =>
  isNumericColumn(col) || footerMarksNumeric(col, !!totaledIds?.has(String(col?.columnId)))

/**
 * CC-53905 (spec 155) — can the "Show $ on amounts" toggle change anything on
 * this grid? True when some column `showDollarNumeric` counts would take a "$"
 * under ON (`currencyEligible`) or already shows one that OFF would strip (its own
 * source marks it, `currencyMarkedColumnIds`).
 *
 * When false, both grids leave the toggle out and publish no explicit Show $
 * choice to the export (`currencyOverride: null`). Without that, a report whose
 * columns CB declares N / F / '' only (Client Snapshot, the aging reports) kept a
 * checkbox that changed nothing on screen and still switched CSV / Excel from the
 * authoritative server export to the client build (ReportExport.jsx, CC-50004).
 *
 * Review fix 4: `totaledIds` is required for the answer to match what the
 * export views do — without it a LEFT / CENTER C column CB totals (52's Fees
 * Collected, 176's Amount) is missed while both views still act on it.
 *
 * @param {object[]} columns           the grid's columns (CB's visible ones)
 * @param {Set<string>} [marked]       `currencyMarkedColumnIds(report)`
 * @param {Set<string>} [totaledIds]   `numericTotalColumnIds(report)`
 */
export const currencyToggleActs = (columns, marked, totaledIds) =>
  (columns || []).some((c) => showDollarNumeric(c, totaledIds) && (currencyEligible(c, marked) || !!marked?.has(String(c?.columnId))))

// --- Row-exclusion toggles (CC-49318 / CC-49319) --------------------------
// Client-side row filters layered on the loaded report data, shared so the flat
// and grouped grids behave identically.

/**
 * Zero-balance row test (CC-49318). A row is "zero balance" when it carries at
 * least one numeric column and EVERY numeric column is 0 or blank — i.e. there's
 * nothing meaningful to show. Rows with no numeric value are never zero-balance
 * (the toggle shouldn't hide them). `getNum(colId)` returns the row's numeric
 * value for a column (or null).
 */
export const isZeroBalanceRow = (numericColIds, getNum) => {
  if (!numericColIds || numericColIds.length === 0) return false
  let sawValue = false
  for (const colId of numericColIds) {
    const n = getNum(colId)
    if (n != null) {
      sawValue = true
      if (n !== 0) return false
    }
  }
  return sawValue // had numeric values and all of them were 0
}

/** Ids of the columns CB declares DECIMAL or INTEGER (spec 133, Hide $0 guard). */
export const declaredAmountColumnIds = (columns = []) =>
  columns.filter(isDeclaredAmount).map((c) => String(c.columnId))

/**
 * The "Hide $0 / zero-balance rows" test both grids apply (spec 133). It is
 * `isZeroBalanceRow` over the numeric columns — the rule unchanged — AND no
 * column CB declares DECIMAL / INTEGER holds a non-zero value.
 *
 * Why the second half: CB left-aligns most of the money on WIP - AR - Trust
 * (155) — 12 of its 16 DECIMAL columns, Trust Bal, Total AR and Open Credits
 * among them — so the numeric set was the four right-aligned WIP / billed-fees
 * columns alone, and a matter
 * with $0.00 WIP and $5,000.00 in trust read as a "zero-balance row". Measured on
 * the live run: 1,164 hidden rows held $5,934,426.45 of trust balance.
 *
 * It is a conjunction with today's rule, so it can only hide FEWER rows, never
 * more; it does not make those columns numeric anywhere else (no new toggle, no
 * Show $, no export change).
 */
export const isHideableZeroRow = (numericColIds, amountColIds, getNum) => {
  if (!isZeroBalanceRow(numericColIds, getNum)) return false
  for (const colId of amountColIds || []) {
    const n = getNum(colId)
    if (n != null && n !== 0) return false
  }
  return true
}

const INACTIVE_STATUS_RE = /\b(inactive|closed|terminated|archived)\b/i
// Match "status" as a whole word only (CC-49319, tightened per PR #14 review):
// the bare `state`/`active` substrings false-matched "State" (address), "Estate"
// (estate law!), "Statement Date", and a literal "Active/Inactive" column — which
// put the toggle on the wrong report keyed to the wrong column. \bstatus\b cleanly
// catches "Status", "Matter Status", "Case Status" without the false positives.
const STATUS_LABEL_RE = /\bstatus\b/i

/**
 * Find a column that looks like a matter status (CC-49319) so the
 * inactive/closed toggle can key off it. Returns the column or null. Frontend
 * can only act on a status signal that's actually in the row data — when there's
 * no such column the toggle stays hidden (full coverage needs a cb-api filter).
 */
export const findStatusColumn = (columns = []) =>
  columns.find((c) => STATUS_LABEL_RE.test(c?.label || '')) || null

/** Whether a status cell's text reads as inactive/closed (CC-49319). */
export const isInactiveStatusValue = (text) => INACTIVE_STATUS_RE.test(stripHtml(text))

// --- Per-column value picker (CC-49317) -----------------------------------
// A multi-select dropdown of a column's distinct values, offered IN ADDITION to
// the forgiving free-text filter on name-type columns (client / matter /
// timekeeper / attorney / user). The column's filter value becomes an object
// { text, picked } so both coexist; non-picker columns keep a plain string.

// CC-49733: firms label the same concept "Responsible Attorney" OR
// "Responsible Lawyer" — match both so either wording gets the value picker.
const NAME_COLUMN_RE = /(client|matter|timekeeper|attorney|lawyer|originator|user|responsible|biller|paralegal)/i

/**
 * Whether a column should offer the value picker (CC-49317): a non-numeric
 * name-type column (the free-text filter still applies to everything).
 */
export const isNameColumn = (col) => !isNumericColumn(col) && NAME_COLUMN_RE.test(col?.label || '')

/** Sorted, de-duped, non-empty display values for a picker (HTML stripped). */
export const distinctDisplayValues = (displays) => {
  const set = new Set()
  for (const d of displays) {
    const s = stripHtml(d).trim()
    if (s) set.add(s)
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

/** Whether a column filter value (string OR { text, picked }) is active. */
export const filterIsActive = (fv) => {
  if (fv == null) return false
  if (typeof fv === 'string') return fv.trim() !== ''
  return !!(fv.text && fv.text.trim() !== '') || (Array.isArray(fv.picked) && fv.picked.length > 0)
}

/**
 * Match a shown cell value against a column filter value that may be a plain
 * string (free-text only) or { text, picked } (CC-49317 picker + free-text).
 * The picked set is an EXACT membership test on the stripped display; the text
 * uses the forgiving matcher. Both must pass when both are present.
 *
 * Spec 171: an object value may also carry `mode` (Starts with / Ends with),
 * which anchors the TEXT half only — picks stay exact membership. A bare string
 * and an object with no (or an unknown) `mode` are Contains, exactly as before.
 * This is the ONE predicate every consumer of the filter reaches: the flat
 * grid's TanStack `filterFn` (ReportDataGrid), and the grouped grid's
 * `passesOther` → `rowPasses`, which the rendered rows, the SUM-only totals
 * recompute and the export / AI view all read — so the figures always describe
 * the rows on screen, whatever the operator.
 */
export const matchColumnFilter = (shown, fv) => {
  if (!filterIsActive(fv)) return true
  if (typeof fv === 'string') return forgivingMatch(shown, fv)
  const { text, picked } = fv
  if (Array.isArray(picked) && picked.length > 0 && !picked.includes(stripHtml(shown).trim())) return false
  if (text && text.trim() !== '') return matchByMode(shown, text, columnFilterMode(fv))
  return true
}

/**
 * Human-readable "what is this column" string for the header definition tooltip
 * (CC-49302), built from the existing metadata — e.g. "Number · totaled as Sum",
 * "Date". Returns '' when there's nothing meaningful to say.
 */
const NUMERIC_FILTER_TYPES = new Set(['NUMBER', 'NUMBERPICKER'])
const AGGREGATE_PHRASE = { SUM: 'summed', AVG: 'averaged', MIN: 'minimum', MAX: 'maximum', COUNT: 'counted' }

// --- Aging / inactivity windows (CC-49327) ---------------------------------
// "Show me matters not billed/active in N days." A client-side row filter keyed
// to a date column that reads as a last-activity signal ("Last Bill Date",
// "Last Payment", "Last Activity", …). Shared so flat + grouped grids agree.

const AGING_LABEL_RE =
  /\blast\s+(bill(?:ed)?|payment|paid|activity|active|invoice(?:d)?|charge(?:d)?|work(?:ed)?|time|entry)\b|\bdays\s+since\b/i

const MS_PER_DAY = 86400000

/**
 * Parse a date out of a cell's display text. Guards against bare numbers
 * (Date.parse("1200") reads as the YEAR 1200 — a currency column named "Last
 * Billed Amount" must never pass): the text must contain a date separator or a
 * month name before we let Date.parse at it. Returns epoch ms or null.
 */
export const parseCellDate = (text) => {
  const s = stripHtml(text).trim()
  if (!s) return null
  if (!/[/-]/.test(s) && !/[a-z]{3}/i.test(s)) return null
  const t = Date.parse(s)
  return Number.isFinite(t) ? t : null
}

/**
 * Find the column the aging window should key off (CC-49327), or null. Label
 * heuristic only — callers must ALSO verify the column's data actually parses
 * as dates (the CC-49319 bug-#4 lesson: never offer a control that can't act).
 */
export const findAgingColumn = (columns = []) =>
  columns.find((c) => AGING_LABEL_RE.test(c?.label || '')) || null

/**
 * Whether a row qualifies as "stale" for an N-day inactivity window: its aging
 * date is more than N days ago. A BLANK/unparseable cell counts as stale — a
 * matter with no "Last Bill Date" has never been billed, which is exactly what
 * "not billed in 60 days" is hunting for.
 */
export const isStaleValue = (text, days, now = Date.now()) => {
  const t = parseCellDate(text)
  if (t == null) return true
  return now - t >= days * MS_PER_DAY
}

/**
 * Detect a numeric column's display format from a sample formatted cell
 * (CC-49337): whether it renders as currency and how many decimals it shows.
 * Used to write REAL Excel number formats on export, so cells stay summable
 * numbers AND look like the report ("$1,234.56", not "1234.56").
 */
export const detectNumberFormat = (sample) => {
  const text = stripHtml(sample ?? '').trim()
  const currency = /\$/.test(text)
  const percent = /%/.test(text)
  const m = text.match(/\.(\d+)/)
  return { currency, percent, decimals: m ? m[1].length : (currency ? 2 : 0) }
}

// A plain formatted number as CB (or an adapter) displays it: an optional sign or
// opening parenthesis, digits with or without thousands separators, optional
// decimals, and the matching closing parenthesis.
const PLAIN_NUMBER_RE = /^([-(]?)(\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?|\.\d+)(\)?)$/

/**
 * The CC-49341 per-report "Show $ on amounts" toggle, for ONE cell: put a "$" on
 * the number the cell already displays, and change nothing else.
 *
 * CC-53905 (spec 155): this used to re-format the number at exactly 2 decimals —
 * from the cell's dataValue, or, for a subtotal / total (which has none here), by
 * re-parsing CB's already-rounded display. So a C0 total CB displays "1,541,996"
 * (dataValue 1541996.25) read "$1,541,996.00", cents invented from a rounded
 * figure, and an N1 hours cell "5,069.4" read "$5,069.36". CB has ALREADY rounded
 * the display to the column's declared precision (CellsFormatDecimals), so the
 * symbol goes onto those exact digits: no digit is added, removed, re-rounded or
 * re-grouped. "1,541,996" → "$1,541,996" (V1's "C0"); "-99" → "-$99";
 * "(1,234.56)" → "($1,234.56)"; "2700.00" → "$2700.00".
 *
 * Whether the column may carry a "$" at all is `currencyEligible`'s decision, not
 * this function's. Returns the display unchanged when it already carries "$" or
 * "%", is blank, or is not a plain number (an em dash, a label, "2845d").
 */
export const withCurrencySymbol = (display) => {
  const text = stripHtml(display ?? '').trim()
  // Never $-ify a cell that already shows a currency symbol, OR a percentage
  // (a "% Change" column is numeric+RIGHT-aligned but must not become "$12.50").
  if (/[$%]/.test(text)) return display
  const m = text.match(PLAIN_NUMBER_RE)
  if (!m) return display
  // An unbalanced parenthesis is not a number CB formatted; leave it alone.
  if ((m[1] === '(') !== (m[3] === ')')) return display
  return `${m[1]}$${m[2]}${m[3]}`
}

/**
 * CC-49743: the inverse of withCurrencySymbol — drop the $ from a cell whose
 * server display carries it (e.g. P&L amounts), so "Show $ on amounts" works
 * both ways. Only ever called on numeric cells, where a literal $ can only be
 * the currency marker; HTML wrappers (e.g. colored spans) pass through intact.
 */
export const stripCurrencySymbol = (display) => String(display ?? '').replace(/\$\s?/g, '')

/** Whether a display value visibly carries a currency symbol. */
export const displayHasCurrency = (display) => /\$/.test(stripHtml(display ?? ''))

// --- Thousands separators (spec 211) ----------------------------------------
//
// Every report figure of 1,000 or more shows a comma every three digits LEFT of
// the decimal point. CB already groups its N and C formatted columns
// ("1,095.9", "2,330.00" — measured on reports 190, 44, 49, 52, 121, 155, 222),
// but its F / unformatted columns and every CSV-backed specialized report
// (Timekeeper Analysis: "384322.50") arrive bare. The digits are never touched:
// only the separators are inserted, and a value that already has them passes
// through unchanged.

// The standard's exceptions — identifiers, places and pages — named by the
// column header. Their digits are a label, not a quantity: live CSVs carry
// Balance Sheet "Account Number" 10101 and General Ledger "Ref Number" 123456,
// both of which look numeric to the CSV adapter, and the 1099 reports carry
// "Tax ID" / "Account Number" / "Zip Code". A count ("# of Matters", "Number of
// Entries") and "No Charge Hours" are quantities, so they are not matched.
const IDENTIFIER_LABEL_RE = new RegExp([
  '#(?!\\s*of\\b)',
  '\\b(?:number|nbr|num)\\b(?!\\s+of\\b)',
  '\\bno\\.(?!\\s*of\\b)',
  '\\bno\\s*$',
  '\\b(?:id|ids|code|codes|key|ref|reference|seq|sequence)\\b',
  '\\b(?:zip|zipcode|postal|address|phone|fax|page|pages|ssn|ein|tin)\\b',
].join('|'), 'i')

// A year column keeps a bare four-digit year ("2026"), but an amount in a
// "Prior Year" column ("125000.00") is still a quantity and is grouped.
const YEAR_LABEL_RE = /\b(?:year|yr|fy)\b/i
const BARE_YEAR_RE = /^\s*\d{4}\s*$/

// One plain number as CB or an adapter prints it, split so only the INTEGER run
// is regrouped: leading space, "(" or "-", "$", "-", the integer digits, the
// decimals, ")", "%", trailing space.
const UNGROUPED_NUMBER_RE = /^(\s*)([-(]?)(\$?)(-?)(\d+)(\.\d+)?(\)?)(%?)(\s*)$/

const groupNumberText = (text, keepYears) => {
  const m = UNGROUPED_NUMBER_RE.exec(text)
  if (!m) return text
  const [, lead, open, dollar, minus, int, frac = '', close, pct, trail] = m
  // Nothing to group below 1,000; a leading zero ("000123") is a padded code.
  if (int.length < 4 || int[0] === '0') return text
  if ((open === '(') !== (close === ')')) return text
  if (keepYears && BARE_YEAR_RE.test(text)) return text
  return `${lead}${open}${dollar}${minus}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${frac}${close}${pct}${trail}`
}

/**
 * Insert thousands separators into a cell's display HTML: "384322.50" →
 * "384,322.50", "-1631.30" → "-1,631.30", "($2860.10)" → "($2,860.10)". Only
 * text OUTSIDE tags is read, and only a text run that is one whole plain number,
 * so CB's markup, a date ("12/27/2025"), a label ("PTD"), an address and a
 * grouped value ("1,095.9") all come back byte for byte. Decimals are never
 * grouped (spec 211's rule: commas go left of the point only).
 *
 * Whether a column takes separators at all is `groupsThousands`'s decision.
 *
 * @param {string} html
 * @param {{ keepYears?: boolean }} [opts] leave a bare four-digit year alone
 */
export const withThousandsSeparators = (html, { keepYears = false } = {}) => {
  const raw = String(html ?? '')
  if (!raw.includes('<')) return groupNumberText(raw, keepYears)
  return raw
    .split(/(<[^>]*>)/)
    .map((part) => (part.startsWith('<') ? part : groupNumberText(part, keepYears)))
    .join('')
}

const columnHeader = (col) => String(col?.rawLabel ?? col?.label ?? '')

/**
 * Spec 211 — does this column's figures take thousands separators?
 *
 *  - never a column CB declares TEXT / DATE / DATETIME / BOOL / GUID — "Invoice #"
 *    on reports 44 and 222 is TEXT and right-aligned, its "6238" an identifier;
 *  - never a column whose header names an identifier, place or page
 *    (IDENTIFIER_LABEL_RE) — the CSV adapter infers "numeric" from the digits, so
 *    the header is the only signal it has;
 *  - otherwise yes for a column CB declares DECIMAL / INTEGER, whatever its
 *    alignment (209 of the 418 numeric columns in the vladdemo census are
 *    numeric by that declaration alone), and for any column the grids already call
 *    numeric (`showDollarNumeric`: alignment, a numeric filter type, or a numeric
 *    grand total).
 *
 * The census (all 119 generic definitions, 958 columns) has no identifier or year
 * among the columns this admits. It changes display only — alignment, Show $,
 * Hide $0, Group By and the export's number classification are untouched.
 *
 * @param {object} col                a grid column
 * @param {Set<string>} [totaledIds]  `numericTotalColumnIds(report)`
 */
export const groupsThousands = (col, totaledIds) =>
  !isDeclaredNonNumeric(col) &&
  !IDENTIFIER_LABEL_RE.test(columnHeader(col)) &&
  (isDeclaredAmount(col) || showDollarNumeric(col, totaledIds))

/**
 * `withThousandsSeparators` for a column already admitted by `groupsThousands`,
 * keeping a bare year when the header names one. The client PDF calls it on the
 * text cells of a column its view flags `thousands` (a left-aligned DECIMAL the
 * export writes as text), so paper reads like the screen.
 */
export const thousandsText = (html, header) =>
  withThousandsSeparators(html, { keepYears: YEAR_LABEL_RE.test(String(header ?? '')) })

// A plain number that already carries separators, split like UNGROUPED_NUMBER_RE.
const GROUPED_NUMBER_RE = /^(\s*)([-(]?)(\$?)(-?)(\d{1,3}(?:,\d{3})+)(\.\d+)?(\)?)(%?)(\s*)$/

const ungroupNumberText = (text) => {
  const m = GROUPED_NUMBER_RE.exec(text)
  return m ? text.replace(m[5], m[5].replace(/,/g, '')) : text
}

/**
 * Spec 212 — the inverse of `withThousandsSeparators`, for "Show commas in
 * numbers" OFF: "1,234,567.89" → "1234567.89", "($2,860.10)" → "($2860.10)". It
 * strips CB's own separators too, so OFF means no separators anywhere a figure
 * is shown. The same text-run rule applies — only a run that is one whole
 * grouped number — so "Smith, John" or a "1,2,3" list is never touched, and no
 * digit is ever changed.
 */
export const withoutThousandsSeparators = (html) => {
  const raw = String(html ?? '')
  if (!raw.includes('<')) return ungroupNumberText(raw)
  return raw
    .split(/(<[^>]*>)/)
    .map((part) => (part.startsWith('<') ? part : ungroupNumberText(part)))
    .join('')
}

/**
 * A cell, subtotal or total display as its column shows it: with spec 211's
 * separators when the column takes them (`groupsThousands`) and the report's
 * "Show commas in numbers" is ON (spec 212, the default), with every separator
 * stripped when it is OFF. A column that does not take them (an identifier, a
 * date, text) is returned unchanged either way.
 *
 * @param {object} col
 * @param {string} html
 * @param {Set<string>} [totaledIds]  `numericTotalColumnIds(report)`
 * @param {boolean} [show=true]       the report's "Show commas in numbers" state
 */
export const groupedDisplay = (col, html, totaledIds, show = true) => {
  if (!groupsThousands(col, totaledIds)) return html
  return show ? thousandsText(html, columnHeader(col)) : withoutThousandsSeparators(html)
}

// A figure with at least four integer digits, grouped or not ("1234", "1,234.50").
const FOUR_DIGIT_FIGURE_RE = /(^|[^\d,.])\d(?:\d{3,}|\d{0,2}(?:,\d{3})+)(?![\d,])/

/**
 * Spec 212 — can "Show commas in numbers" change anything on this report? True
 * when some column `groupsThousands` admits holds a figure of 1,000 or more in a
 * cell, subtotal or total (grouped by CB or not). When false the toggle is left
 * out, as Show $ is (`currencyToggleActs`): a checkbox that changes nothing on
 * screen is a control that lies.
 *
 * @param {object} report               the grid contract ({ reportData, reportFooterCells, reportFooterRows? })
 * @param {object[]} columns            the grid's columns
 * @param {Set<string>} [totaledIds]    `numericTotalColumnIds(report)`
 */
export const thousandsToggleActs = (report, columns, totaledIds) => {
  const ids = new Set((columns || []).filter((c) => groupsThousands(c, totaledIds)).map((c) => String(c.columnId)))
  if (ids.size === 0) return false
  const hit = (cells) => (cells || []).some((c) => c && ids.has(String(c.columnId)) && FOUR_DIGIT_FIGURE_RE.test(stripHtml(c.displayValue)))
  const walk = (rows) => {
    for (const r of rows || []) {
      if (r?.isGroup) {
        if (hit(r.groupFooterCells) || (r.groupFooterRows || []).some(hit) || walk(r.dataRows)) return true
      } else if (hit(r?.dataCells)) {
        return true
      }
    }
    return false
  }
  return walk(report?.reportData) || hit(report?.reportFooterCells) || (report?.reportFooterRows || []).some(hit)
}

/** A count for a toolbar label ("1,234 records"), grouped like every figure. */
export const formatCount = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-US') : String(n ?? ''))

/** The preset windows offered by the aging control (CC-49327). */
export const AGING_PRESET_DAYS = [30, 60, 90, 120, 180, 365] // 120/180/365 added per QA (bug 7a)

/**
 * Widest an AUTO-SIZED report column may grow, in px.
 *
 * The report tables use `min-w-full` with the browser's auto table layout, which
 * sizes every column to the widest content across ALL rendered rows. One long
 * value therefore starves the rest: on Activities by Matter (142) with the full
 * result set rendered, Description took 1,910px and pushed the table to 3,331px
 * inside an 1,855px container, squeezing "Start Date/Time" down to 116px so it
 * wrapped onto two lines. It reads as broken padding; it's really one runaway
 * column.
 *
 * `break-words` alone (CC-49460) doesn't help — it wraps text only once the cell
 * is already constrained, it does not constrain anything. A `max-width` on the
 * cell does: measured on report 208, capping a 527px column at 150px settled it
 * at 195px (min-content), and the freed width redistributed to its neighbours.
 *
 * 640 is chosen to be inert on normal reports — the widest legitimate column
 * observed across the catalog is ~530px (Flat Fee Bill Allocations' Matter) — so
 * this only bites when a column has genuinely run away.
 *
 * A column the USER has explicitly resized is never capped; the cap is dropped
 * from both the th and the td as soon as a stored width exists.
 *
 * ⚠️ CORRECTED 2026-09-01 (CC-53347). That sentence used to end "their width wins",
 * and on a WIDE grouped report it did not. Dropping the cap was the only thing a
 * resize actually did there: the table was `min-w-full` (width:auto), and an
 * auto-width table with `table-layout: fixed` discards every inline th width once
 * its content overflows. So the requested number was ignored and the column jumped
 * to full content width instead — wider, in the direction of the content, and not
 * where the user put it. The grouped grid now takes a DEFINITE width (`w-full`) once
 * any user width exists, which is what makes fixed layout real; see spec 106.
 *
 * Consequence for this constant: once a resize has happened on a grouped report,
 * every visible column carries a seeded width, so nothing is auto-sized and this
 * cap no longer participates. It still governs the untouched state, which is the
 * state 86 grouped reports are in until someone drags something.
 */
export const MAX_AUTO_COLUMN_PX = 640

// --- Filter label resolution (CC-50009) ------------------------------------
//
// Report 303 carries two columns for one concept: the filterable LOOKUP is
// labelled `RespAtty`, while a display-only sibling is labelled
// `Responsible Attorney`. The filter panel rendered the raw label, so it read
// RESPATTY (amplified by the `uppercase` transform) while the grid header and the
// Group By dropdown — reading the SAME `label` field off the sibling — read fine.
//
// The friendly label is already in the panel's own props; it was being discarded
// with the non-filterable columns. This resolves it, in two tiers, and refuses to
// guess beyond them.

/** Does `abbrev` read like a field code rather than a written label? */
const isCodeLikeLabel = (s) => {
  const t = String(s || '').trim()
  if (!t || /\s/.test(t)) return false // a spaced label is already human
  // camelCase / PascalCase with an internal hump ("RespAtty"), or a SCREAMING code.
  return /[a-z][A-Z]/.test(t) || (/^[A-Z0-9_]+$/.test(t) && t.length > 3)
}

/**
 * Is `full` a plausible expansion of the abbreviation `abbrev`? Requires every
 * letter of the abbreviation to appear IN ORDER in the candidate — so
 * "RespAtty" → "Responsible Attorney" passes, and an unrelated longer label does
 * not. Deliberately strict: a wrong label on a financial filter is worse than an
 * ugly one.
 */
export const isLabelExpansion = (abbrev, full) => {
  const a = String(abbrev || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const f = String(full || '').toLowerCase().replace(/[^a-z0-9]/g, '')
  if (!a || !f || f.length <= a.length) return false
  let i = 0
  for (const ch of f) {
    if (ch === a[i]) i += 1
    if (i === a.length) return true
  }
  return false
}

/**
 * The label to show for a filter column, preferring a sibling column that
 * describes the same field with a friendlier name.
 *
 * Tier 1 — same `field`: unambiguous, the two columns are provably the same data,
 * so the longer spaced label wins. No guessing involved.
 * Tier 2 — the filter's own label is code-like AND exactly ONE sibling label is an
 * ordered expansion of it. Requiring exactly one match is what keeps this safe; two
 * candidates means we don't actually know, so we leave the raw label alone.
 *
 * Note the real fix for report 303 is to correct the label in the report
 * definition — one row, no code. This exists because the same abbreviation problem
 * will recur on other tenants' reports, and rendering a raw field code at a user is
 * never right.
 */
/*
 * Display-only filter-label aliases.
 *
 * The wire is untouched — this changes only what a human reads. Everything that
 * shows a filter label resolves through resolveFilterLabel: the pill trigger, the
 * expanded panel, the applied-criteria chip, and the export/print "Filters — …"
 * provenance line (ReportView.jsx builds `filtersSummary` from the same pills).
 * That single path is what CC-50009 established, and it is why an alias here
 * cannot leave the export header disagreeing with the screen.
 *
 * "Entry Dates" is CB's own metadata label for the standard date-range filter.
 * Renamed to "Date Picker" per Jordan, 2026-08-21.
 *
 * Tradeoff accepted deliberately: this names the CONTROL rather than the field it
 * filters, so a report that carried two date filters would render two pills with
 * the same label. Keep the map keyed on the exact CB label rather than matching
 * "any date filter", so the blast radius stays exactly one label.
 */
const FILTER_LABEL_ALIASES = new Map([['entry dates', 'Date Picker']])

export const resolveFilterLabel = (col, allColumns = []) => {
  const raw = String(col?.label ?? '')
  if (!raw) return raw
  // Before the twin-expansion logic below: an alias is an explicit override and
  // must not be beaten by a longer sibling label CB happens to carry.
  const alias = FILTER_LABEL_ALIASES.get(raw.trim().toLowerCase())
  if (alias) return alias
  const others = allColumns.filter((c) => c !== col && String(c?.label ?? '').trim())
  const spacedAndLonger = (c) => /\s/.test(c.label) && c.label.length > raw.length

  const sameField = others.filter(
    (c) => String(c.field ?? '') !== '' && String(c.field) === String(col?.field ?? ' ') && spacedAndLonger(c)
  )
  if (sameField.length > 0) return sameField[0].label

  if (!isCodeLikeLabel(raw)) return raw
  const expansions = others.filter((c) => spacedAndLonger(c) && isLabelExpansion(raw, c.label))
  return expansions.length === 1 ? expansions[0].label : raw
}

// --- User-selectable Group By (CC-49458) ------------------------------------
// Regroup a report client-side by any column: build the same shape CB's server
// grouping produces (isGroup rows + groupFooterCells) so ReportGroupedGrid
// renders it unchanged. Subtotals are computed ONLY for columns that are true
// plain sums (the CC-49382 SUM-only guardrail, via summableColumns) — an
// average/rate column gets NO synthetic subtotal rather than a wrong one.

/** Recursively collect all leaf (non-group) rows. */
export const collectLeafRows = (rows) => {
  const out = []
  for (const r of rows || []) {
    if (r.isGroup) out.push(...collectLeafRows(r.dataRows))
    else out.push(r)
  }
  return out
}

const groupCell = (cells, id) => cells?.find((c) => String(c.columnId) === String(id))

/** Columns eligible as a Group By target: visible, non-numeric, labeled. */
export const groupableColumns = (columns = []) =>
  columns.filter((c) => c.visible !== false && !c.isBlankColumn && !isNumericColumn(c) && (c.label || '').trim())

/**
 * Regroup `report` by the column `groupColId`. Works for flat AND
 * server-grouped reports (server groups are flattened first, so "Group by"
 * behaves consistently across reports — the CC-49458 ask). Returns a NEW
 * report object; the grand-total reportFooterCells pass through unchanged
 * (they're scope-independent).
 */
export const groupFlatReport = (report, groupColId) => {
  if (!report || groupColId == null) return report
  const leaves = collectLeafRows(report.reportData)
  if (leaves.length === 0) return report

  // SUM-only classification against the server grand totals.
  const serverTotals = new Map()
  for (const c of report.reportFooterCells || []) {
    const n = toNumber(c.displayValue)
    if (n != null) serverTotals.set(String(c.columnId), n)
  }
  const allValues = new Map()
  for (const colId of serverTotals.keys()) {
    const vals = []
    for (const lf of leaves) {
      const cell = groupCell(lf.dataCells, colId)
      const n = toNumber(cell?.dataValue != null ? cell.dataValue : cell?.displayValue)
      if (n != null) vals.push(n)
    }
    allValues.set(colId, vals)
  }
  const aggregateByCol = new Map((report.columns || []).map((c) => [String(c.columnId), columnAggregate(c)]))
  const summable = summableColumns(allValues, serverTotals, aggregateByCol)
  const footerSample = new Map((report.reportFooterCells || []).map((c) => [String(c.columnId), c.displayValue]))

  // Bucket leaves by the group column's display text.
  const buckets = new Map()
  for (const lf of leaves) {
    const key = stripHtml(groupCell(lf.dataCells, groupColId)?.displayValue ?? '').trim() || '(blank)'
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(lf)
  }

  const groups = [...buckets.entries()]
    // CC-49753: group order uses the shared comparator (digits before letters).
    .sort(([a], [b]) => compareCellValues(a, b))
    .map(([key, rows]) => ({
      isGroup: true,
      // Spec 193: `key` is DECODED text (stripHtml), and groupHeaderContent is
      // rendered as HTML — re-escape it, or a cell that was safely `&lt;img…&gt;`
      // becomes a live element the moment a user groups by its column.
      groupHeaderContent: escapeHtml(key),
      dataRows: rows,
      groupFooterCells: [...summable].map((colId) => {
        const sum = rows.reduce((acc, r) => {
          const cell = groupCell(r.dataCells, colId)
          const n = toNumber(cell?.dataValue != null ? cell.dataValue : cell?.displayValue)
          return acc + (n ?? 0)
        }, 0)
        return { columnId: Number(colId), displayValue: formatLikeSample(footerSample.get(colId) || '0.00')(sum) }
      }),
    }))

  return { ...report, reportData: groups }
}

/**
 * CC-49948: the report's declared default sort, computed the way V1 computes it.
 *
 * V1 (Views/Reports/ManageReports.cshtml) selects the report's sort columns as
 *   Columns.Where(c => c.Groupable == false && c.SortedBy).OrderBy(c => c.SortOrder)
 * where `SortedBy` is `SortDirection != NONE`. cb2 previously took the FIRST
 * column with any sortDirectionName, which broke two ways:
 *
 *  - It picked up GROUPABLE columns. A groupable column's SortDirection orders
 *    its GROUPS, not the report's rows — that is why Revenue by State (321,
 *    grouped on State) loaded sorted by State ascending while V1 loads it
 *    unsorted.
 *  - It ignored SortOrder, so with several declared sort columns the wrong one
 *    could win.
 *
 * Returns a react-table style sorting array (possibly multi-level, ordered by
 * SortOrder), or [] when the report declares no row sort.
 */
export const metadataDefaultSort = (columns = []) =>
  columns
    .filter(
      (c) =>
        c?.groupable !== true &&
        (c?.sortDirectionName === 'ASC' || c?.sortDirectionName === 'DESC')
    )
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((c) => ({ id: String(c.columnId), desc: c.sortDirectionName === 'DESC' }))

/**
 * CC-49944: the set of column ids that actually carry at least one numeric
 * value, derived from the rows. Metadata alone over-claims — CB right-aligns
 * numbers, and some cb2 adapters right-align EVERY non-label column (Balance
 * Sheet marks `i === 0 ? 'LEFT' : 'RIGHT'`), which made the header tooltip
 * announce "Number" on columns holding only text. Works for flat and grouped
 * reports alike (leaves are collected recursively).
 */
export const columnDataKinds = (report, numericRatio = 0.7) => {
  // Tally, don't short-circuit. The first version promoted a column to 'numeric'
  // the moment ANY cell parsed as a number, which is too eager: Rate Difference's
  // BillingCode holds "Hourly Fee" on almost every row but a handful of numeric
  // codes, and that was enough to describe the whole column as a Number. Same
  // majority rule the other adapters already use (CsvReportGrid.numericCols,
  // BalanceSheetReport.numericHeader) so the three agree.
  const tally = new Map()
  for (const row of collectLeafRows(report?.reportData)) {
    for (const cell of row.dataCells || []) {
      const key = String(cell.columnId)
      let t = tally.get(key)
      if (!t) { t = { numeric: 0, text: 0 }; tally.set(key, t) }
      const raw = cell.dataValue != null ? cell.dataValue : cell.displayValue
      if (toNumber(raw) != null) t.numeric += 1
      else if (stripHtml(raw).trim() !== '') t.text += 1
    }
  }
  const kinds = new Map()
  for (const [key, t] of tally) {
    const seen = t.numeric + t.text
    kinds.set(key, seen === 0 ? 'empty' : t.numeric / seen >= numericRatio ? 'numeric' : 'text')
  }
  return kinds
}

/**
 * The declared footer aggregate for a column PLUS the scope it applies to.
 * CB carries these separately: a grouped report can total a column in each
 * group subtotal without totalling it at the report level, so the tooltip must
 * not promise "the report total" for a group-only aggregate (CC-49944).
 */
export const columnAggregateScoped = (col) => {
  const r = col?.reportFooterAggregateName
  if (r && r !== 'NONE') return { agg: r, scope: 'report' }
  const g = col?.groupFooterAggregateName
  if (g && g !== 'NONE') return { agg: g, scope: 'group' }
  return { agg: null, scope: null }
}

const AGGREGATE_SCOPE_PHRASE = { report: 'in the report total', group: 'in each group subtotal' }

/**
 * Friendly header tooltip from the existing metadata (CC-49302): the column type,
 * how it rolls up in the totals row, and the sort/filter affordances. A real
 * "how it's calculated" definition needs a stored per-column description (a new
 * model) — tracked as a follow-up (CC-49420).
 *
 * CC-49944 fixed three defects here:
 *  1. It claimed "Number" on columns holding no numbers, because right-alignment
 *     alone was treated as proof. Alignment is now only a fallback for columns CB
 *     declared no type for, and the rows override it either way (`dataKind`).
 *  2. It returned '' — no tooltip at all — on columns CB declares no filter type
 *     for and does not right-align (e.g. Rate Difference). The rows now supply
 *     the type instead, so those columns get a real definition rather than a
 *     generic one bolted onto every header.
 *  3. It announced a group-level aggregate as if it were the report total
 *     (e.g. Flat Fee Bill Allocations). The scope now follows the metadata.
 *
 * @param col           the CB column metadata
 * @param opts.dataKind 'numeric' | 'text' | 'empty' from columnDataKinds, or
 *                      undefined when the rows haven't been inspected (metadata
 *                      is then trusted, preserving the pre-CC-49944 behavior).
 */
export const columnDefinition = (col, { dataKind, hasTotal, sample } = {}) => {
  if (!col) return ''
  // An explicitly declared type is authoritative.
  const declared = FILTER_TYPE_LABEL[col.filterTypeName]
  const declaredNumeric = NUMERIC_FILTER_TYPES.has(col.filterTypeName)
  // With no declared type: believe the rows; fall back to alignment only when
  // the rows haven't been inspected.
  const inferredNumeric =
    !declared && (dataKind === 'numeric' || (dataKind === undefined && col.columnAlignment === 'RIGHT'))
  const numeric = declaredNumeric || inferredNumeric
  // A column holding only text is "Text"; an entirely empty column has nothing
  // worth saying, so it keeps no tooltip at all.
  const type = declared || (inferredNumeric ? 'Number' : dataKind === 'text' ? 'Text' : null)
  if (!type) return ''
  let sentence = type
  // CC-49944 round 2: the declared aggregate is NOT trustworthy on its own. CB
  // stamps reportFooterAggregateName = SUM on every column of some reports — on
  // Rate Difference (178) all 19 columns claim SUM including "Entry Description",
  // and the report renders NO totals at all (every reportFooterCells value is an
  // empty string). Flat Fee Bill Allocations (208) totals exactly ONE column but
  // six claimed "summed in the report total". Restricting the claim to numeric
  // columns was not enough, because identifier columns (Entry Number "001129",
  // Matter Number "1974.0001") parse as numbers.
  //
  // So the aggregate is announced only when the report ACTUALLY produces a total
  // for this column — `hasTotal`, derived from a non-empty footer cell. That is
  // ground truth rather than a promise the metadata cannot keep. When the caller
  // doesn't supply it (undefined), the declared metadata is trusted as before.
  const aggregateAllowed = numeric && hasTotal !== false
  const { agg, scope } = aggregateAllowed ? columnAggregateScoped(col) : { agg: null, scope: null }
  if (agg && AGGREGATE_PHRASE[agg]) sentence += `, ${AGGREGATE_PHRASE[agg]} ${AGGREGATE_SCOPE_PHRASE[scope]}`
  // CC-49944 QA test 7: show how the column is formatted, for the types where
  // formatting is the question (currency / decimals / date style). Text columns
  // are skipped — an example name teaches nothing and just adds noise. The
  // example doubles as filter guidance: the per-column filter matches this
  // formatted text, not the underlying value (CC-49379).
  const showsExample = sample && (numeric || type === 'Date')
  const example = showsExample ? ` e.g. ${sample}` : ''
  return `${sentence}.${example} Click to sort; type to filter.`
}

/**
 * CC-49944 (QA test 7): a representative FORMATTED value per column, taken from
 * the rows exactly as CB formatted them. The tooltip shows it as an example so
 * the user can see the currency symbol / decimal places / date format a column
 * uses — which also tells them what to type, because the per-column filter
 * matches the formatted display text (CC-49379), not the raw value.
 *
 * First non-empty display value wins; HTML is stripped and long text truncated
 * so a description column can't turn the tooltip into a paragraph. Stops as soon
 * as every column has one.
 */
export const columnSamples = (report, kinds = null, maxLen = 24) => {
  const samples = new Map()
  const wanted = new Set((report?.columns || []).map((c) => String(c.columnId)))
  for (const row of collectLeafRows(report?.reportData)) {
    for (const cell of row.dataCells || []) {
      const key = String(cell.columnId)
      if (samples.has(key)) continue
      const text = stripHtml(cell.displayValue).trim()
      if (!text) continue
      // The example must agree with the type the tooltip states, or the tooltip
      // contradicts itself ("Number. e.g. Hourly Fee" — seen on Rate Difference's
      // BillingCode, a mostly-text column with a few numeric codes). On a numeric
      // column, skip values that aren't numbers and wait for one that is.
      if (kinds?.get(key) === 'numeric' && toNumber(text) == null) continue
      samples.set(key, text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text)
    }
    if (wanted.size > 0 && wanted.size === samples.size) break
  }
  return samples
}

/**
 * CC-49944: ids of columns the report actually renders a total for — a footer
 * cell with a non-empty value. Both grids feed this to `columnDefinition` so a
 * tooltip never promises a total the report does not show. Group-scoped
 * aggregates count too: a grouped report can subtotal a column per group
 * without a report-level total.
 */
export const totaledColumnIds = (report) => {
  const ids = new Set()
  const add = (cells) => {
    for (const c of cells || []) {
      if (String(c?.displayValue ?? '').trim() !== '') ids.add(String(c.columnId))
    }
  }
  add(report?.reportFooterCells)
  for (const r of report?.reportFooterRows || []) add(r)
  const walk = (rows) => {
    for (const r of rows || []) {
      if (!r?.isGroup) continue
      add(r.groupFooterCells)
      walk(r.dataRows)
    }
  }
  walk(report?.reportData)
  return ids
}

/**
 * CC-49852: export filenames follow V1's convention (ReportsController.
 * DownloadReport): "{yyyyMMdd_hhmmss}_{Report_Name}.{ext}" — the report name
 * keeps its casing with every space/special character swapped for '_', and the
 * timestamp uses the legacy 12-hour clock ("hhmmss"). Client-built "current
 * view" exports append "_view" so they can't be mistaken for the
 * authoritative server file.
 */
export const exportFileName = (reportName, ext, { view = false } = {}) => {
  const d = new Date()
  const p2 = (n) => String(n).padStart(2, '0')
  const h12 = d.getHours() % 12 || 12
  const ts = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}_${p2(h12)}${p2(d.getMinutes())}${p2(d.getSeconds())}`
  const safe = String(reportName || 'Report')
    .replace(/[\s<>:"\/|?*.]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'Report'
  return `${ts}_${safe}${view ? '_view' : ''}.${ext}`
}
